import { useState, useEffect } from "react";
import { twMerge } from "tailwind-merge";

import { Button } from "ui/button";
import { DialogActions, DialogBackButton } from "ui/dialog";
import { IconButton } from "ui/icon-button";
import EmailDesignPreview from "containers/admin/clients/touchpoint/components/email-design-preview/email-design-preview";
import { SkeletonLoader } from "components/modals/create-parent-touchpoint-modal/components/skeleton-loader";

import { useParentTouchpointContext } from "components/modals/create-parent-touchpoint-modal/context/parent-touchpoint-context";
import { useCreateParentTouchpointModal } from "components/modals/create-parent-touchpoint-modal/hooks/use-create-parent-touchpoint-modal";
import {
  useCreateParentTouchpointPreview,
  TouchpointPreviewData,
} from "components/modals/create-parent-touchpoint-modal/hooks/use-parent-touchpoint-preview";

import Icons from "assets";

const ParentTouchpointPreview = () => {
  const { handleGenerateEmail, handleEmailChange } = useCreateParentTouchpointModal();

  const {
    generateError,
    isGenerating,
    isTransitioning,
    generatedEmails,
    currentEmailIndex,
    progressText,
  } = useParentTouchpointContext();

  const emailToRender = generatedEmails[currentEmailIndex];
  const isInErrorState = generateError && !isGenerating && !isTransitioning;
  // Show arrows when we have multiple emails OR when in error state with previous emails to go back to
  const showArrows = generatedEmails.length > 1 || (isInErrorState && generatedEmails.length > 0);
  const canShowParentEmail = emailToRender && !isGenerating && !isTransitioning && !isInErrorState;
  const isParentEmailInvalid =
    isInErrorState || !emailToRender || !emailToRender.html || !emailToRender.beeJson;

  const { fetchStudentEmail } = useCreateParentTouchpointPreview();
  const [studentEmail, setStudentEmail] = useState<TouchpointPreviewData>({});

  const isStudentLoading = !studentEmail.finalHtml || !studentEmail.beeJson;
  const isParentBusy = isTransitioning || isGenerating;

  useEffect(() => {
    const loadStudentEmail = async () => {
      const data = await fetchStudentEmail();
      setStudentEmail(data);
    };
    loadStudentEmail();
    // Intentionally once on mount; fetchStudentEmail is recreated each render from the hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only
  }, []);

  const previewColumnsClassName = twMerge(
    "flex gap-4 p-1 pt-[2px] min-w-0",
    isStudentLoading && "min-h-full",
  );

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex justify-between items-center mb-1.5">
        <p className="text-md font-bold">Email preview</p>
        <div className="flex items-center pr-1">
          <Button
            variant="text"
            size="small"
            startIcon={<Icons.Refresh />}
            onClick={handleGenerateEmail}
            disabled={isGenerating}>
            <span>Regenerate</span>
          </Button>
          {showArrows && (
            <div className="flex items-center gap-3 ml-1">
              <IconButton
                variant="contained"
                aria-label="Previous email"
                size="small"
                onClick={() =>
                  isInErrorState
                    ? handleEmailChange(generatedEmails.length - 1) // Go to last successful email from error state
                    : handleEmailChange(Math.max(0, currentEmailIndex - 1))
                }
                disabled={(!isInErrorState && currentEmailIndex === 0) || isGenerating}>
                <Icons.ChevronDown customClass="rotate-90" />
              </IconButton>
              <IconButton
                variant="contained"
                aria-label="Next email"
                size="small"
                onClick={() =>
                  handleEmailChange(Math.min(generatedEmails.length - 1, currentEmailIndex + 1))
                }
                disabled={
                  isInErrorState || currentEmailIndex === generatedEmails.length - 1 || isGenerating
                }>
                <Icons.ChevronDown customClass="-rotate-90" />
              </IconButton>
            </div>
          )}
        </div>
      </div>
      <div className="w-full min-w-0 mx-auto flex flex-col flex-1 min-h-0">
        <div className="flex gap-4 pr-3 pl-1 pb-[6px] flex-shrink-0 min-w-0 items-center min-h-[30px]">
          <div className="flex-1 min-w-0 max-w-[400px] text-left text-sm">Student</div>
          <div className="flex-1 min-w-0 max-w-[400px] flex items-center text-left text-sm gap-2">
            Parent
          </div>
        </div>
        <div className="flex-1 overflow-y-scroll custom-scroll min-h-0">
          <div className={previewColumnsClassName}>
            <div className="flex-1 min-w-0 max-w-[400px] relative">
              {isStudentLoading && <SkeletonLoader />}
              {!isStudentLoading && studentEmail.finalHtml && studentEmail.beeJson && (
                <EmailDesignPreview
                  htmlContent={studentEmail.finalHtml}
                  htmlJSON={studentEmail.beeJson}
                  isCommentMode={false}
                  mobileView={true}
                />
              )}
            </div>
            <div className="flex-1 min-w-0 max-w-[400px] relative min-h-[160px]">
              {isParentBusy && (
                <div className="absolute inset-0 z-10 border border-aqua-600 bg-gray-100 shadow shadow-aqua-100 overflow-hidden">
                  <SkeletonLoader />
                  {isGenerating && (
                    <div
                      role="status"
                      aria-live="polite"
                      className="absolute inset-x-0 top-0 z-20 flex flex-col items-start gap-1.5 bg-gray-100 pl-4 pr-3 pt-3 pb-2.5">
                      <div className="flex w-full items-start gap-2">
                        <Icons.Spinner className="mt-0.5 size-4 shrink-0 animate-spin text-aqua-600" />
                        <p className="min-w-0 flex-1 text-left text-sm font-medium text-navy-900">
                          {progressText || "Generating email draft..."}
                        </p>
                      </div>
                      <p className="w-full shrink-0 text-left text-xs font-normal text-gray-600">
                        This may take a few moments, please keep this window open.
                      </p>
                    </div>
                  )}
                </div>
              )}
              {canShowParentEmail && emailToRender.html && emailToRender.beeJson && (
                <div className="border border-aqua-600 shadow shadow-aqua-100 relative">
                  <EmailDesignPreview
                    htmlContent={emailToRender.html}
                    htmlJSON={emailToRender.beeJson}
                    isCommentMode={false}
                    mobileView={true}
                  />
                </div>
              )}
              {!isParentBusy && isParentEmailInvalid && (
                <div className="h-full min-h-[350px] relative border border-gray-200 shadow shadow-gray-100 bg-gray-50 p-6">
                  <div className="sticky top-1/2 -translate-y-1/2 flex flex-col items-center justify-center text-center">
                    <div className="flex items-center gap-2 mb-2 text-gray-900">
                      <Icons.ExclamationCircle className="text-gray-600" />
                      <h3>Failed to generate parent email</h3>
                    </div>
                    <p className="text-sm text-gray-600">
                      If the issue persists, submit a{" "}
                      <a
                        href="https://forms.office.com/Pages/ResponsePage.aspx?id=wRB1Zxt7uUma8sDyknoBp8ygRRK6ZfNOkD0-04SHFnVUMDJVR0xENzFLVTNRUThHV1VHMlRBV0cySS4u"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold text-critical-700 underline hover:text-critical-800">
                        support ticket
                      </a>
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ParentTouchpointPreviewActions = () => {
  const { handleStepChange } = useCreateParentTouchpointModal();
  const { handleCreateEmail } = useCreateParentTouchpointPreview();
  const { generateError, isGenerating, isTransitioning, generatedEmails, currentEmailIndex } =
    useParentTouchpointContext();

  const isInErrorState = generateError && !isGenerating && !isTransitioning;
  const isCreateEmailDisabled = Boolean(
    isGenerating || isInErrorState || !generatedEmails[currentEmailIndex],
  );

  return (
    <DialogActions
      type="steps"
      back={<DialogBackButton onClick={() => handleStepChange(2)}>Back to prompt</DialogBackButton>}
      secondary={
        <Button
          size="large"
          variant="outlined"
          onClick={() => handleCreateEmail("toBuilder")}
          disabled={isCreateEmailDisabled}>
          Open in editor
        </Button>
      }
      primary={
        <Button
          size="large"
          onClick={() => handleCreateEmail("toTouchpoint")}
          disabled={isCreateEmailDisabled}>
          Create email
        </Button>
      }
    />
  );
};

export default ParentTouchpointPreview;
